import { ROUTES_MAP } from 'app/shared/utils/routes';

import {
  AlertWorkflowFormModel,
  CaseWorkflowFormModel,
  CreateAlertWorkflowPayload,
  CreateCaseWorkflowPayload,
  DisplayButtons,
  OwningTeamAvailability,
  QueueSelectionButtons,
  TeamSelectionButtons,
  WhiteListButtons,
  WorkflowContexts,
} from 'app/modules/workflowButtonConfig/models';
import { Disposition } from 'app/modules/dispositions/response';
import { DispositionType } from 'app/modules/dispositions/models';

// Utils
import { heapTrack } from 'app/shared/utils/heap';
import heapEvents from 'app/shared/utils/heapEvents';
import type {
  ColorColorSchema,
  U21Palette,
} from 'vendor/material-minimal/palette';

// /workflows/buttons/(alerts|cases)'
export const getWorkflowButtonsListPath = (tab: WorkflowContexts): string => {
  // there are only alert and case workflow tabs at the moment
  if (tab === WorkflowContexts.ALERT) {
    return ROUTES_MAP.alertWorkflowButtons.path;
  }
  return ROUTES_MAP.caseWorkflowButtons.path;
};

// /workflows/buttons/(alerts|cases)/:id'
export const getWorkflowButtonDetailsPath = (
  tab: WorkflowContexts,
  id: number | string,
): string => {
  const templatePath =
    tab === WorkflowContexts.ALERT
      ? ROUTES_MAP.alertWorkflowButton.path
      : ROUTES_MAP.caseWorkflowButton.path;
  return templatePath.replace(':id', String(id));
};

export const getRouteWorkflowContext = (route: string): WorkflowContexts => {
  if (route.startsWith('/workflows/buttons/alerts')) {
    return WorkflowContexts.ALERT;
  }
  return WorkflowContexts.CASE;
};

export const formatAlertWorkflowPayload = (
  model: AlertWorkflowFormModel,
): CreateAlertWorkflowPayload => {
  const {
    owning_team_availability: owningTeamAvailability,
    whitelist_time: { value, unit } = {},
    ...rest
  } = model;
  const payload: CreateAlertWorkflowPayload = rest;

  if (payload.whitelist === WhiteListButtons.NONE) {
    payload.whitelist_duration = undefined;
  }

  if (payload.whitelist_duration === WhiteListButtons.SPECIFIC) {
    payload.whitelist_time = String(value);
    payload.whitelist_time_units = unit;
  }

  /* Alert Specific */
  if (payload.queue_availability === QueueSelectionButtons.SHOW_IN_ALL_QUEUES) {
    payload.queue = [];
  }
  if (payload.team_availability === TeamSelectionButtons.SHOW_FOR_ALL_TEAMS) {
    payload.teams = [];
  }
  if (owningTeamAvailability === OwningTeamAvailability.ALL_TEAMS) {
    payload.owning_team_ids = null;
  } else if (
    owningTeamAvailability === OwningTeamAvailability.RESTRICT_BY_TEAM
  ) {
    payload.owning_team_ids = payload.owning_team_ids ?? [];
  }

  payload.disposition_id ||= null;

  return payload;
};

export const formatCaseWorkflowPayload = (
  model: CaseWorkflowFormModel,
): CreateCaseWorkflowPayload => {
  const {
    owning_team_availability: owningTeamAvailability,
    whitelist_time: { value, unit } = {},
    ...rest
  } = model;
  const payload: CreateCaseWorkflowPayload = rest;

  if (payload.whitelist === WhiteListButtons.NONE) {
    payload.whitelist_duration = undefined;
  }

  if (payload.whitelist_duration === WhiteListButtons.SPECIFIC) {
    payload.whitelist_time = String(value);
    payload.whitelist_time_units = unit;
  }
  /* Case Specific */
  if (payload.display_specific === DisplayButtons.DO_NOTHING) {
    payload.teams = [];
    payload.masking_tags = [];
    payload.display_tags = [];
  }
  if (payload.queue_availability === QueueSelectionButtons.SHOW_IN_ALL_QUEUES) {
    payload.queue = [];
  }
  if (payload.team_availability === TeamSelectionButtons.SHOW_FOR_ALL_TEAMS) {
    payload.teams = [];
  }
  if (owningTeamAvailability === OwningTeamAvailability.ALL_TEAMS) {
    payload.owning_team_ids = null;
  } else if (
    owningTeamAvailability === OwningTeamAvailability.RESTRICT_BY_TEAM
  ) {
    payload.owning_team_ids = payload.owning_team_ids ?? [];
  }

  payload.disposition_id ||= null;

  return payload;
};

export const submitHeapTrack = (
  id: string | undefined,
  templateId: string | undefined,
  context: string,
  data: AlertWorkflowFormModel | CaseWorkflowFormModel,
) =>
  heapTrack(id ? heapEvents.workflows.edit : heapEvents.workflows.create, {
    context,
    description: data.description,
    label: data.label,
    id,
    templateId,
  });

export const getButtonColor = (
  palette: U21Palette,
  color: ColorColorSchema,
) => ({
  color: palette.colors[color].contrastText,
  background: palette.colors[color].main,
  hoverBackground: palette.colors[color].dark,
});

export const isDispositionValid = (
  selectedDispositionId: number | undefined,
  dispositions: Disposition[],
  allowedClassifications: DispositionType[],
): boolean => {
  if (!selectedDispositionId) {
    return false;
  }
  const selectedDisposition = dispositions.find(
    (disposition) => disposition.id === selectedDispositionId,
  );
  if (
    !selectedDisposition ||
    !allowedClassifications.includes(selectedDisposition.type)
  ) {
    return false;
  }
  return true;
};
